import { clear, h } from '../core/dom'
import { haptic } from '../core/haptics'
import { go } from '../core/router'
import {
  addDose,
  getState,
  nightsSorted,
  removeDose,
  sleepTrend,
  toISODate,
  type NightEntry,
} from '../core/store'
import { num, t } from '../i18n'
import { sheet, shortDate, topbar } from './shared'

/**
 * The ledger is the part of NabuSu that outlives the night.
 *
 * In withdrawal, memory of how bad things were is unreliable in both directions
 * — three terrible weeks and three terrible months feel the same from inside.
 * A written record is the only way anyone, including a prescriber, can tell
 * whether things are actually moving.
 *
 * It records. It never advises. There is no suggested taper anywhere in here.
 */
export function renderLedger(root: HTMLElement): void {
  const paint = (): void => {
    clear(root)
    const d = t()
    const nights = nightsSorted().slice(0, 30)
    const doses = getState().doses.slice(-20).reverse()
    const trend = sleepTrend()

    const trendText =
      trend === 'better'
        ? d.ledger.trendBetter
        : trend === 'worse'
          ? d.ledger.trendWorse
          : trend === 'flat'
            ? d.ledger.trendFlat
            : d.ledger.notEnough

    root.append(
      topbar(d.ledger.title),
      h(
        'div',
        { class: 'stack fade-in' },
        h('p', { class: 'faint' }, d.ledger.sub),

        nights.length > 0 &&
          h(
            'div',
            { class: 'card' },
            h('p', { class: 'faint', style: { marginBottom: '10px' } }, trendText),
            ...nights.map(nightRow),
          ),

        nights.length === 0 && h('p', { class: 'dim' }, d.ledger.empty),

        h(
          'button',
          {
            class: 'btn block',
            onclick: () => {
              haptic.tap()
              doseSheet(paint)
            },
          },
          d.ledger.addDose,
        ),

        doses.length > 0 &&
          h(
            'div',
            { class: 'card' },
            h('p', { class: 'section-label', style: { marginBottom: '6px' } }, d.ledger.dose),
            ...doses.map((dose) =>
              h(
                'div',
                { class: 'dose-row' },
                h('span', { class: 'night-date' }, shortDate(dose.date)),
                h('span', { class: 'grow' }, dose.medication),
                h('span', { class: 'dim' }, `${num(dose.amountMg)} ${d.ledger.unit}`),
                h(
                  'button',
                  {
                    class: 'icon-btn',
                    'aria-label': d.common.cancel,
                    onclick: () => {
                      haptic.tap()
                      removeDose(dose.id)
                      paint()
                    },
                  },
                  '×',
                ),
              ),
            ),
          ),

        h(
          'button',
          {
            class: 'btn ghost block',
            onclick: () => {
              haptic.tap()
              void exportForDoctor()
            },
          },
          d.ledger.export,
        ),

        h(
          'button',
          {
            class: 'btn ghost block',
            onclick: () => {
              haptic.tap()
              go('checkin')
            },
          },
          d.home.checkin,
        ),
      ),
    )
  }

  paint()
}

function nightRow(night: NightEntry): HTMLElement {
  const d = t()
  const hours = night.sleptHours ?? 0
  // Bar is scaled against 9 hours, so a full-width bar means a genuinely good
  // night rather than "the best of a bad set".
  const width = Math.min(100, (hours / 9) * 100)

  return h(
    'div',
    { class: 'night-row' },
    h('span', { class: 'night-date' }, shortDate(night.date)),
    h(
      'span',
      { class: 'night-bar' },
      h('i', {
        style: {
          width: `${width}%`,
          opacity: String(0.35 + (night.quality ?? 3) * 0.13),
        },
      }),
    ),
    h('span', { class: 'night-hours' }, night.sleptHours != null ? `${num(hours)}${d.ledger.hours}` : '—'),
    h(
      'span',
      { class: 'night-flags' },
      night.rescueSessions > 0 ? '🌒' : '',
      night.symptoms.length > 0 ? '•' : '',
    ),
  )
}

function doseSheet(onDone: () => void): void {
  sheet((close) => {
    const d = t()
    const med = h('input', { type: 'text', placeholder: 'Lorazepam', maxLength: 40 })
    const amount = h('input', { type: 'number', step: '0.05', min: '0', placeholder: '0.5' })
    const date = h('input', { type: 'date', value: toISODate(new Date()) })

    return h(
      'div',
      { class: 'stack' },
      h('h2', {}, d.ledger.addDose),
      h('div', {}, h('label', { class: 'field' }, d.ledger.medication), med),
      h(
        'div',
        {},
        h('label', { class: 'field' }, `${d.ledger.amount} (${d.ledger.unit})`),
        amount,
      ),
      h('div', {}, h('label', { class: 'field' }, d.ledger.dose), date),
      h(
        'div',
        { class: 'row' },
        h(
          'button',
          {
            class: 'btn ghost grow',
            onclick: () => {
              haptic.tap()
              close()
            },
          },
          d.common.cancel,
        ),
        h(
          'button',
          {
            class: 'btn primary grow',
            onclick: () => {
              const name = med.value.trim()
              const mg = Number(amount.value)
              if (!name || !Number.isFinite(mg) || mg <= 0) return
              haptic.tap()
              addDose({ date: date.value, medication: name, amountMg: mg })
              close()
              onDone()
            },
          },
          d.common.save,
        ),
      ),
      h('p', { class: 'faint' }, d.settings.dataSub),
    )
  })
}

/**
 * Plain text, not JSON — this is meant to be pasted into a message to a doctor
 * or read off a screen in a ten-minute appointment.
 */
async function exportForDoctor(): Promise<void> {
  const d = t()
  const state = getState()
  const nights = nightsSorted()

  const lines: string[] = [
    'NabuSu — sleep and symptom record',
    `Exported: ${new Date().toISOString().slice(0, 16).replace('T', ' ')}`,
    `Nights recorded: ${nights.length}`,
    '',
    'Date | Hours slept | Quality 1-5 | Woke in night | Night rescues | Symptoms',
  ]

  for (const n of nights) {
    lines.push(
      [
        n.date,
        n.sleptHours ?? '-',
        n.quality ?? '-',
        n.wokeInNight == null ? '-' : n.wokeInNight ? 'yes' : 'no',
        n.rescueSessions,
        n.symptoms.length ? n.symptoms.join(', ') : '-',
      ].join(' | '),
    )
    if (n.note) lines.push(`    note: ${n.note}`)
  }

  if (state.doses.length) {
    lines.push('', 'Doses as recorded by the patient:')
    for (const dose of state.doses) {
      lines.push(`${dose.date} | ${dose.medication} | ${dose.amountMg} mg`)
    }
  }

  lines.push(
    '',
    'This record was written by the patient in a self-tracking app.',
    'It contains no clinical assessment and no medication advice.',
  )

  const text = lines.join('\n')
  try {
    await navigator.clipboard.writeText(text)
    alertLine(d.ledger.exported)
  } catch {
    // Clipboard is blocked in some in-app browsers; fall back to a share sheet,
    // and if that is unavailable too, show the text so it can be copied by hand.
    if (navigator.share) {
      try {
        await navigator.share({ text })
        return
      } catch {
        /* user dismissed */
      }
    }
    sheet(() =>
      h(
        'div',
        { class: 'stack' },
        h('h2', {}, d.ledger.export),
        h('textarea', { rows: 14, value: text, readOnly: true }),
      ),
    )
  }
}

function alertLine(message: string): void {
  const el = h(
    'div',
    {
      class: 'card fade-in',
      style: {
        position: 'fixed',
        insetInline: '20px',
        bottom: '28px',
        zIndex: '60',
        textAlign: 'center',
        maxWidth: '520px',
        marginInline: 'auto',
      },
    },
    message,
  )
  document.body.appendChild(el)
  setTimeout(() => el.remove(), 2600)
}
